import { useEffect, useState } from "react";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { AddressBook, ArrowLeft, CaretRight, GearSix, Moon, SignOut, Sun } from "@phosphor-icons/react";
import { ApiFehler, lies, schick, setzeAbmeldeHandler } from "./api/client";
import type { Ich } from "./api/types";
import { Anmelden } from "./screens/Anmelden";
import { Projekte } from "./screens/Projekte";
import { ProjektSeite } from "./screens/projekt/ProjektSeite";
import { BesprechungSeite } from "./screens/BesprechungSeite";
import { BegehungSeite } from "./screens/BegehungSeite";
import { TagSeite } from "./screens/TagSeite";
import { Adressen } from "./screens/Adressen";
import { Verwaltung } from "./screens/Verwaltung";
import { schalteThema, thema } from "./lib/thema";
import { PfadAnbieter, usePfadLesen } from "./lib/pfad";
import { Netzstand, offlineSpeicherLeeren } from "./components/Netzstand";
import { Dialog } from "./components/Bausteine";
import { alleVerwerfen, setzeBesitzer, vorgemerkt } from "./lib/warteschlange";

export function useIch() {
  return useQuery({ queryKey: ["ich"], queryFn: () => lies<Ich>("/me") });
}

/** Abgemeldet: alles verwerfen, nicht nur „ich“ — auf einem geteilten Baustellen-Tablet
 *  sähe die nächste Person sonst kurz die Projekte der vorigen aus dem Zwischenspeicher.
 *  Aber NICHT per `clear()`: das entfernt auch die gerade laufende Abfrage „ich“, und
 *  TanStack bricht eine entfernte Abfrage still ab — sie meldet nie ein Ergebnis, `App`
 *  bleibt bei `isPending` und zeigt eine weiße Seite statt der Anmeldung (jede noch nicht
 *  angemeldete Person, z. B. nach einer Einladung; Rückmeldung 26.10.2026).
 *  Die Offline-Warteschlange bleibt: sie gehört der Person, die sie erfasst hat
 *  (lib/warteschlange.ts), und geht erst in deren nächster Sitzung hinaus. */
function abgemeldetVerwerfen(qc: QueryClient): void {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "ich" });
  qc.setQueryData(["ich"], null);
  offlineSpeicherLeeren();
}

export function App() {
  const qc = useQueryClient();
  const ich = useIch();

  useEffect(() => {
    setzeAbmeldeHandler(() => abgemeldetVerwerfen(qc));
  }, [qc]);

  if (ich.isPending) return null;
  const abgemeldet = ich.data == null || (ich.error instanceof ApiFehler && ich.error.status === 401);
  // Beim Rendern, nicht im Effekt: die Kopfleiste (Netzstand) arbeitet in IHREM Effekt die
  // Warteschlange ab, und Effekte der Kinder laufen vor denen der Eltern.
  setzeBesitzer(abgemeldet ? null : ich.data!.id);
  if (abgemeldet) {
    return (
      <Routes>
        <Route path="*" element={<Anmelden />} />
      </Routes>
    );
  }
  return (
    <PfadAnbieter>
      <Kopf ich={ich.data!} />
      <Routes>
        <Route path="/" element={<Projekte />} />
        <Route path="/p/:projektId/*" element={<ProjektSeite />} />
        <Route path="/b/:besprechungId" element={<BesprechungSeite />} />
        <Route path="/g/:begehungId" element={<BegehungSeite />} />
        <Route path="/t/:tagId" element={<TagSeite />} />
        <Route path="/adressen" element={["admin", "buero"].includes(ich.data!.rolle) ? <Adressen /> : <Navigate to="/" replace />} />
        <Route path="/verwaltung" element={<Verwaltung />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PfadAnbieter>
  );
}

function Kopf({ ich }: { ich: Ich }) {
  const qc = useQueryClient();
  const [t, setT] = useState(thema());
  const [wartet, setWartet] = useState(0);
  const abmelden = async () => { await schick("/auth/logout"); abgemeldetVerwerfen(qc); };
  return (
    <header className="kopf">
      <Link to="/" className="marke">
        <img src="/logo.svg" alt="LILASp" />
        <span>Bauleitung</span>
      </Link>
      <Weg />
      <div className="kopf-luft" />
      <Netzstand />
      {["admin", "buero"].includes(ich.rolle) && (
        <Link to="/adressen" className="knopf leise kopf-link" title="Adressbuch – Firmen und Ansprechpersonen aller Projekte">
          <AddressBook size={18} /><span>Adressbuch</span></Link>
      )}
      <Link to="/verwaltung" className="knopf leise kopf-link" title={`${ich.email} – Konto${ich.rolle === "admin" ? " und Benutzer" : ""}`}>
        <GearSix size={18} /><span>{ich.anzeigename || ich.email}</span></Link>
      <button className="knopf leise symbol" aria-label="Hell oder dunkel"
              onClick={() => setT(schalteThema())}>
        {t === "dark" ? <Sun size={18} /> : <Moon size={18} />}
      </button>
      <button className="knopf leise symbol" aria-label="Abmelden"
              onClick={async () => { const n = (await vorgemerkt()).length; if (n) setWartet(n); else await abmelden(); }}>
        <SignOut size={18} />
      </button>
      <Dialog offen={wartet > 0} zu={() => setWartet(0)} titel="Noch nicht übertragen" fuss={<>
        <button className="knopf leise" onClick={() => setWartet(0)}>Abbrechen</button>
        <button className="knopf leise gefahr" onClick={async () => {
          if (!confirm("Die offline erfassten Einträge löschen? Sie sind dann verloren.")) return;
          await alleVerwerfen(); setWartet(0); await abmelden();
        }}>Verwerfen und abmelden</button>
        <button className="knopf haupt" onClick={async () => { setWartet(0); await abmelden(); }}>Trotzdem abmelden</button>
      </>}>
        <p style={{ marginTop: 0 }}>
          {wartet === 1 ? "Ein offline erfasster Eintrag wartet" : `${wartet} offline erfasste Einträge warten`} noch
          auf die Übertragung.</p>
        <p className="mute">Sie bleiben auf diesem Gerät und gehen hinaus, sobald Sie sich wieder anmelden und Netz
          da ist – nur unter Ihrem Konto, nicht unter dem der nächsten Person.</p>
      </Dialog>
    </header>
  );
}

/** Brotkrumen und Zurück-Pfeil (lib/pfad.tsx). Auf der Startseite leer. */
function Weg() {
  const pfad = usePfadLesen();
  const zurueck = [...pfad].reverse().find((s) => s.nach);
  if (!pfad.length) return null;
  return (
    <nav className="weg" aria-label="Navigation">
      {zurueck?.nach && (
        <Link to={zurueck.nach} className="knopf leise symbol weg-zurueck" aria-label={`Zurück zu ${zurueck.titel}`}>
          <ArrowLeft size={18} />
        </Link>
      )}
      <Link to="/" className="weg-stufe weg-start">Projekte</Link>
      {pfad.map((s, i) => (
        <span key={i} className="weg-teil">
          <CaretRight size={12} className="weg-trenner" />
          {s.nach ? <Link to={s.nach} className="weg-stufe">{s.titel}</Link>
                  : <span className="weg-stufe aktuell" aria-current="page">{s.titel}</span>}
        </span>
      ))}
    </nav>
  );
}
